/**
 * Avatar chooser. SPEC.md §46, §6.3, §19.
 *
 * The ARIA radiogroup pattern rather than a styled <input type="radio">: the
 * choice has to be announced as aria-checked, arrow keys have to walk it on a
 * desktop browser, and the selected tile has to be recognisable from its ring and
 * its tick — never from its colour alone. §19
 *
 * The set itself comes from the shared AVATARS so the keys stored against a
 * player row can never drift from what this renders. §46
 *
 * **One character per player.** Whoever is already at the table has theirs, and
 * those tiles are disabled rather than hidden: a grid that quietly lost eleven of
 * its hundred and twelve would leave a player wondering where the one they wanted
 * went, and would renumber the rest under them while they looked. Disabled says
 * the true thing — it exists, somebody has it. The server enforces the same rule
 * on the way in, so this is presentation and never the guarantee. §46, §39
 */

import { useEffect, useRef, type KeyboardEvent } from 'react';
import { AVATARS, avatarFor, isAvatarTaken } from '@card-counter/shared';
import { Avatar } from '@/components/ui/Avatar';

export interface AvatarPickerProps {
  value: string;
  onChange(avatarKey: string): void;
  /** Id of the visible group label, which becomes the radiogroup's name. §19 */
  labelId?: string;
  disabled?: boolean;
  /**
   * Avatars already spoken for at this table. §46
   *
   * The viewer's own is never in effect taken — `value` is exempted below — so a
   * player editing their profile is not told they cannot keep what they have.
   */
  takenKeys?: readonly string[];
}

export function AvatarPicker({
  value,
  onChange,
  labelId,
  disabled = false,
  takenKeys = [],
}: AvatarPickerProps) {
  const tiles = useRef<Array<HTMLButtonElement | null>>([]);

  // avatarFor() falls back to the default, so an unrecognised stored key still
  // leaves exactly one tabbable tile rather than none. §46
  const selectedKey = avatarFor(value).key;
  const selectedIndex = Math.max(
    0,
    AVATARS.findIndex((avatar) => avatar.key === selectedKey),
  );

  const isTaken = (key: string): boolean => isAvatarTaken(key, takenKeys, selectedKey);

  /**
   * Bring a tile into the grid, and move **nothing else**.
   *
   * `scrollIntoView` was the obvious way to write this and it is the wrong one:
   * it walks every scrollable ancestor, so scrolling the grid to a character far
   * down the set also scrolls the *page* by however much it takes — 71 px on a
   * 320 px phone, 194 px when a software keyboard has shortened the viewport.
   * The create screen puts the chosen character's enlarged preview above this
   * grid, and that is what those pixels take away: at 320 px the top third of it
   * goes past the edge, and on a short viewport the whole thing does, so the one
   * piece of feedback a pick produces plays where nobody can see it. §18.2
   *
   * This is `block: 'nearest'` by hand, against the grid's own scroller: nothing
   * happens at all when the tile is already inside it. §19, §18.4
   */
  function revealTile(tile: HTMLButtonElement | null | undefined): void {
    const box = tile?.parentElement;
    if (!tile || !box) return;
    const tileBox = tile.getBoundingClientRect();
    const scrollBox = box.getBoundingClientRect();
    if (tileBox.top < scrollBox.top) {
      box.scrollTop += tileBox.top - scrollBox.top;
    } else if (tileBox.bottom > scrollBox.bottom) {
      box.scrollTop += tileBox.bottom - scrollBox.bottom;
    }
  }

  /**
   * The set is far taller than the box that holds it, so the selected tile is
   * usually below the fold — including on first paint, where a player sees a
   * grid with nothing marked and concludes they have not chosen yet. It matters
   * more when the host edits somebody: opening the sheet on a player whose
   * character is a hundred rows down the list must not look like it lost their
   * avatar.
   */
  useEffect(() => {
    revealTile(tiles.current[selectedIndex]);
    // Mount only: re-running it on every change would fight a player who has
    // scrolled away from their own selection to look at the rest of the set.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Moves the selection, skipping whatever is already somebody else's. §46, §19
   *
   * `step` rather than an index, because a taken tile has to be walked *past* in
   * the direction the player asked for — landing on it and stopping would make
   * an arrow key look broken, and skipping to the nearest free one in either
   * direction would make Left and Right sometimes do the same thing. Bounded by
   * the set size, so a table that somehow held every avatar simply does nothing.
   */
  function step(from: number, delta: number): void {
    for (let tried = 1; tried <= AVATARS.length; tried += 1) {
      const next = (from + delta * tried + AVATARS.length * tried) % AVATARS.length;
      const candidate = AVATARS[next]!;
      if (isTaken(candidate.key)) continue;
      onChange(candidate.key);
      // `preventScroll`, then the grid by hand, for the reason revealTile exists:
      // focusing a tile below the fold otherwise scrolls the page to it too.
      tiles.current[next]?.focus({ preventScroll: true });
      revealTile(tiles.current[next]);
      return;
    }
  }

  function onKeyDown(event: KeyboardEvent<HTMLButtonElement>, index: number): void {
    // Selection follows focus, which is how a radio group is expected to behave.
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault();
        step(index, 1);
        return;
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault();
        step(index, -1);
        return;
      case 'Home':
        event.preventDefault();
        // From one before the start, forwards: the first tile that is free.
        step(-1, 1);
        return;
      case 'End':
        event.preventDefault();
        step(AVATARS.length, -1);
        return;
      default:
    }
  }

  return (
    <div
      role="radiogroup"
      aria-labelledby={labelId}
      /*
        The set is long enough now to bury the submit button on a 320 px phone,
        so the tiles scroll within a bounded box and the commit action stays
        where a thumb expects it. §18.4, §19

        **It scrolls in one axis, and the lock has to be written down.**
        `scroll-area` sets `overflow-y`, and CSS then coerces the *other* axis
        from `visible` to `auto` — a box cannot scroll one way and overflow the
        other. So this grid has always been a horizontal scroller too; it simply
        had nothing sticking out of it. Selecting a tile in the **last column**
        gave it something: the tick badge below is offset `-right-1`, four pixels
        past its own tile, and in the last column that lands a couple of pixels
        past the grid's content edge. Measured at 375 px, the same grid reports
        0 px of horizontal overflow with a first-column tile checked and 2.48 px
        with a last-column one — and at 2.48 px it pans. Two pixels is not
        something anybody would call a scroll; it is enough to make the surface
        answer a sideways drag on the one screen where the whole set is meant to
        sit still under a thumb. `overflow-x` is therefore stated rather than
        inherited, because "locked" was never what the CSS actually said.

        The padding is what makes the lock free rather than a trade: hiding the
        overflow would otherwise *clip* what caused it. Six pixels clears the
        badge's four and the checked tile's 2 px ring — box-shadows do not extend
        a scrollable area but they are still cut off by `hidden` — so the
        selection's two markers stay whole in every column. It also fixes the top
        row, which has been clipping the top of its own tick all along: upward
        overflow is not something a scroller can reach, so there those four
        pixels were simply lost rather than pannable. §19
      */
      className="scroll-area grid max-h-56 grid-cols-[repeat(auto-fill,minmax(3rem,1fr))] gap-2 overflow-x-hidden p-1.5"
    >
      {AVATARS.map((avatar, index) => {
        const checked = avatar.key === selectedKey;
        const taken = isTaken(avatar.key);
        return (
          <button
            key={avatar.key}
            ref={(node) => {
              tiles.current[index] = node;
            }}
            type="button"
            role="radio"
            aria-checked={checked}
            /* Spoken, not just shown: "Pikachu, taken" is the whole of why the
               tile does not respond, and a greyed tile alone does not say it. §19 */
            aria-label={taken ? `${avatar.label}, already taken` : avatar.label}
            title={taken ? `${avatar.label} — already taken` : avatar.label}
            disabled={disabled || taken}
            // Roving tabindex: one stop for the whole group. §19
            tabIndex={index === selectedIndex ? 0 : -1}
            onClick={() => onChange(avatar.key)}
            onKeyDown={(event) => onKeyDown(event, index)}
            className={[
              'relative grid aspect-square min-h-tap place-items-center rounded-card border',
              'transition-[transform,border-color] duration-150 ease-out-soft active:scale-95',
              'disabled:pointer-events-none',
              checked
                ? 'border-accent bg-accent-soft ring-2 ring-accent'
                : 'border-line bg-raised hover:border-ink-faint',
              /* Two different reasons a tile cannot be pressed, and they must not
                 look the same: the whole form being submitted dims everything,
                 while a taken avatar is dimmed *and* desaturated, so a glance at
                 a live form reads "these are gone" rather than "nothing works". §19 */
              taken ? 'opacity-40 grayscale' : disabled ? 'opacity-45' : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {/* The button carries the name, so the artwork stays decorative and
                a screen reader hears "Pikachu" once rather than twice. §19 */}
            <Avatar avatarKey={avatar.key} className="size-[82%]" defer />
            {/* A glyph, not just a hue: the selection survives greyscale. §19 */}
            {checked ? (
              <span
                aria-hidden="true"
                className="absolute -top-1 -right-1 grid size-5 place-items-center rounded-full bg-accent text-[0.65rem] font-bold text-accent-ink"
              >
                ✓
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
